import { Suspense, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import type { Patient } from '@medplum/fhirtypes'
import { Paper, Text, Group, Badge, Stack, Loader, Button, ThemeIcon } from '@mantine/core'
import {
  IconPackage,
  IconRadar2,
  IconStethoscope,
  IconUserSearch,
  IconFileText,
} from '@tabler/icons-react'
import invitaeLogo from '/logos/invitae-logo.svg'
import accuReferenceLogo from '/logos/accu-reference-logo.png'
import labcorpLogo from '/logos/labcorp-logo.png'
import exactSciencesLogo from '/logos/exact-sciences-logo.png'
import questLogo from '/logos/quest-diagnostics-logo.png'
import gtiLogo from '/logos/gti-logo.png'
import { useAppFlags } from '@/hooks/useAppFlags'
import { startViewFor } from '@/utils/order-tests-path'
import { InvitaeRequisitionForm } from './InvitaeRequisitionForm'
import { AccuReferenceRequisitionForm } from './AccuReferenceRequisitionForm'
import { LabcorpRequisitionForm } from './LabcorpRequisitionForm'
import { QuestRequisitionForm } from './QuestRequisitionForm'
import { GtiRequisitionForm } from './GtiRequisitionForm'
import { CologuardRequisitionForm } from './CologuardRequisitionForm'
import { RadiologyRequisitionForm } from './RadiologyRequisitionForm'
import { ProcedureReferralForm } from './ProcedureReferralForm'
import { UnspecifiedReferralForm } from './UnspecifiedReferralForm'

type Props = {
  patient: Patient
}

export const OrderTests = ({ patient }: Props) => {
  const location = useLocation()
  const [view, setView] = useState<
    | 'catalog'
    | 'invitae'
    | 'accuReference'
    | 'labcorp'
    | 'quest'
    | 'gti'
    | 'cologuard'
    | 'radiology'
    | 'procedureReferral'
    | 'unspecifiedReferral'
    | 'referralChoice'
  >(() => startViewFor(location.search))

  // Clicking Place order / Referral while already on this screen doesn't change the
  // route, so the submitted form would otherwise stay put. Re-navigating to the exact
  // same URL (a re-click) yields a new location.key but an unchanged path+query — treat
  // that as "start a new order" and reset to the view the URL asks for. Switching
  // between Place order and Referral changes the `start` param, which also resets. An
  // unrelated same-route search-param update elsewhere on the chart (e.g. stripping
  // ?autoJoin / ?newEncounter) leaves `start` alone, so it won't drop an in-progress order.
  const lastUrlRef = useRef(location.pathname + location.search)
  const lastStartRef = useRef(startViewFor(location.search))
  useEffect(() => {
    const url = location.pathname + location.search
    const start = startViewFor(location.search)
    if (lastUrlRef.current === url || lastStartRef.current !== start) {
      setView(start)
    }
    lastUrlRef.current = url
    lastStartRef.current = start
  }, [location])

  return (
    <Sentry.ErrorBoundary
      fallback={(fallbackProps) => {
        const handleReset = () => fallbackProps.resetError()

        return (
          <div className="p-4">
            <Paper p="lg" withBorder>
              <Stack align="center" gap="md">
                <Text fw={600} size="lg">
                  Something went wrong
                </Text>
                <Text size="sm" c="dimmed">
                  An error occurred while loading the order form. Our team has been notified.
                </Text>
                <Button onClick={handleReset}>Try Again</Button>
              </Stack>
            </Paper>
          </div>
        )
      }}
      beforeCapture={(scope) => {
        scope.setTag('feature', 'order-tests')
        scope.setContext('order-tests', {
          patientId: patient.id,
          view,
        })
      }}
    >
      <OrderTestsContent patient={patient} view={view} setView={setView} />
    </Sentry.ErrorBoundary>
  )
}

type ContentProps = {
  patient: Patient
  view:
    | 'catalog'
    | 'invitae'
    | 'accuReference'
    | 'labcorp'
    | 'quest'
    | 'gti'
    | 'cologuard'
    | 'radiology'
    | 'procedureReferral'
    | 'unspecifiedReferral'
    | 'referralChoice'
  setView: (
    view:
      | 'catalog'
      | 'invitae'
      | 'accuReference'
      | 'labcorp'
      | 'quest'
      | 'gti'
      | 'cologuard'
      | 'radiology'
      | 'procedureReferral'
      | 'unspecifiedReferral'
      | 'referralChoice',
  ) => void
}

const OrderTestsContent = ({ patient, view, setView }: ContentProps) => {
  const { procedureReferralsV1 } = useAppFlags<{ procedureReferralsV1?: boolean }>()

  if (view === 'invitae') {
    return (
      <Suspense fallback={<Fallback />}>
        <InvitaeRequisitionForm patient={patient} onBack={() => setView('catalog')} />
      </Suspense>
    )
  }

  if (view === 'accuReference') {
    return (
      <Suspense fallback={<Fallback />}>
        <AccuReferenceRequisitionForm patient={patient} onBack={() => setView('catalog')} />
      </Suspense>
    )
  }

  if (view === 'labcorp') {
    return (
      <Suspense fallback={<Fallback />}>
        <LabcorpRequisitionForm patient={patient} onBack={() => setView('catalog')} />
      </Suspense>
    )
  }

  if (view === 'quest') {
    return (
      <Suspense fallback={<Fallback />}>
        <QuestRequisitionForm patient={patient} onBack={() => setView('catalog')} />
      </Suspense>
    )
  }

  if (view === 'gti') {
    return (
      <Suspense fallback={<Fallback />}>
        {/*
         * Keyed on the patient, deliberately — same reasoning as the Audit tab
         * in PatientChart.utils.tsx. `PatientPage` renders chart sections with
         * no key, so switching patient re-renders an open order form with new
         * props instead of remounting it, and this form's own state does not
         * reset: patient A's selected tests, diagnosis, collection instructions
         * and signature would stay put while the demographics became patient
         * B's. Submitting that faxes GTI a requisition attributing one
         * patient's orders to another, over a real provider signature.
         *
         * Scoped to this form rather than fixed at the render site: the same
         * latent bug exists in every vendor form here, and fixing it
         * registry-wide is a separate change with a much larger blast radius.
         */}
        <GtiRequisitionForm key={patient.id} patient={patient} onBack={() => setView('catalog')} />
      </Suspense>
    )
  }

  if (view === 'cologuard') {
    return (
      <Suspense fallback={<Fallback />}>
        <CologuardRequisitionForm patient={patient} onBack={() => setView('catalog')} />
      </Suspense>
    )
  }

  if (view === 'radiology') {
    return (
      <Suspense fallback={<Fallback />}>
        <RadiologyRequisitionForm patient={patient} onBack={() => setView('catalog')} />
      </Suspense>
    )
  }

  if (view === 'procedureReferral') {
    return (
      <Suspense fallback={<Fallback />}>
        {/* Keyed like the GTI form: the chart keeps this section mounted across a patient
            switch, and a referral must never carry one patient's details to the next. */}
        <ProcedureReferralForm
          key={patient.id}
          patient={patient}
          onBack={() => setView('referralChoice')}
        />
      </Suspense>
    )
  }

  if (view === 'unspecifiedReferral') {
    return (
      <Suspense fallback={<Fallback />}>
        <UnspecifiedReferralForm
          key={patient.id}
          patient={patient}
          onBack={() => setView('referralChoice')}
        />
      </Suspense>
    )
  }

  if (view === 'referralChoice') {
    return (
      <div className="p-4">
        <div className="mb-6">
          <Button variant="subtle" size="compact-sm" pl={0} onClick={() => setView('catalog')}>
            ← Back to catalog
          </Button>
          <h1 className="text-2xl font-bold">Procedures / Referrals</h1>
          <p className="mt-1 text-[#7D7987]">Choose how you want to refer this patient.</p>
        </div>
        <Stack gap="md">
          <Paper
            p="lg"
            withBorder
            className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
            onClick={() => setView('procedureReferral')}
          >
            <Group gap="md" wrap="nowrap" align="flex-start">
              <ThemeIcon size={48} radius="md" variant="light" color="indigo">
                <IconUserSearch size={26} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="lg" mb={4}>
                  Specialist Referral
                </Text>
                <Text size="sm">
                  Find a specialist by name, a nearby clinic, or one your clinic refers to often. An
                  AI draft of the letter and clinical summary is ready for you to review, then the
                  referral is faxed to the provider.
                </Text>
                <Group gap="xs" mt="sm">
                  <Badge size="xs" variant="light" color="indigo">
                    Specialist Search
                  </Badge>
                  <Badge size="xs" variant="light" color="gray">
                    CPT Procedure Codes
                  </Badge>
                  <Badge size="xs" variant="light" color="green">
                    Fax Referral
                  </Badge>
                </Group>
              </div>
            </Group>
          </Paper>
          <Paper
            p="lg"
            withBorder
            className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
            onClick={() => setView('unspecifiedReferral')}
          >
            <Group gap="md" wrap="nowrap" align="flex-start">
              <ThemeIcon size={48} radius="md" variant="light" color="grape">
                <IconFileText size={26} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="lg" mb={4}>
                  Unspecified Referral
                </Text>
                <Text size="sm">
                  Write a referral without choosing a provider — e.g. &ldquo;GI for screening
                  colonoscopy&rdquo; or &ldquo;screening mammogram&rdquo; — with an optional AI
                  draft. The patient downloads it from their portal and takes it to any in-network
                  provider or diagnostic center.
                </Text>
                <Group gap="xs" mt="sm">
                  <Badge size="xs" variant="light" color="grape">
                    No provider selection
                  </Badge>
                  <Badge size="xs" variant="light" color="gray">
                    Patient-carried
                  </Badge>
                  <Badge size="xs" variant="light" color="green">
                    Diagnostic screening or specialist
                  </Badge>
                </Group>
              </div>
            </Group>
          </Paper>
        </Stack>
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Order Tests</h1>
        <p className="mt-1 text-[#7D7987]">Select a test requisition form to begin ordering.</p>
      </div>

      <Stack gap="md">
        {/* Invitae Genetic Testing Card */}
        <Paper
          p="lg"
          withBorder
          className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
          onClick={() => setView('invitae')}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <img
              src={invitaeLogo}
              alt="Invitae"
              className="h-[40px] w-[120px] shrink-0 object-contain"
            />
            <div>
              <Group gap="xs" mb={4}>
                <Text fw={600} size="lg">
                  Invitae
                </Text>
                <Badge color="grape" variant="light" size="sm">
                  Genetic
                </Badge>
              </Group>
              <Text size="sm">
                Comprehensive genetic testing panels including hereditary cancer (BRCA1/2, Lynch,
                colorectal, breast &amp; gyn) and cardiology (arrhythmia, cardiomyopathy,
                aortopathy, lipid disorders, and more). Up to 100 genes per panel.
              </Text>
              <Group gap="xs" mt="sm">
                <Badge size="xs" variant="light" color="blue">
                  Blood / Saliva / Buccal
                </Badge>
                <Badge size="xs" variant="light" color="red">
                  Cardiology panels
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  5-12 day turnaround (STAT)
                </Badge>
                <Badge size="xs" variant="light" color="green">
                  Medicare eligible
                </Badge>
                <Badge
                  size="xs"
                  variant="light"
                  color="orange"
                  leftSection={<IconPackage size={10} />}
                >
                  Mail-order (kit shipped to patient)
                </Badge>
              </Group>
            </div>
          </Group>
        </Paper>

        {/* Accu Reference General Test Requisition Card */}
        <Paper
          p="lg"
          withBorder
          className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
          onClick={() => setView('accuReference')}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <img
              src={accuReferenceLogo}
              alt="AccuReference"
              className="h-[40px] w-[120px] shrink-0 object-contain"
            />
            <div>
              <Group gap="xs" mb={4}>
                <Text fw={600} size="lg">
                  AccuReference
                </Text>
                <Badge color="teal" variant="light" size="sm">
                  Lab
                </Badge>
              </Group>
              <Text size="sm">
                Standard laboratory tests including CBC, CMP, lipid panels, thyroid function,
                molecular diagnostics, microbiology cultures, and cancer markers.
              </Text>
              <Group gap="xs" mt="sm">
                <Badge size="xs" variant="light" color="teal">
                  Blood / Urine / Swab
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  Standard / STAT options
                </Badge>
              </Group>
            </div>
          </Group>
        </Paper>

        {/* Labcorp General Requisition Card */}
        <Paper
          p="lg"
          withBorder
          className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
          onClick={() => setView('labcorp')}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <img
              src={labcorpLogo}
              alt="Labcorp"
              className="h-[40px] w-[120px] shrink-0 object-contain"
            />
            <div>
              <Group gap="xs" mb={4}>
                <Text fw={600} size="lg">
                  Labcorp
                </Text>
                <Badge color="violet" variant="light" size="sm">
                  Lab
                </Badge>
              </Group>
              <Text size="sm">
                Full-service laboratory testing including metabolic panels, hematology, hepatitis,
                thyroid, diabetes screening, microbiology cultures, NuSwab molecular tests, and 100+
                individual tests.
              </Text>
              <Group gap="xs" mt="sm">
                <Badge size="xs" variant="light" color="violet">
                  Blood / Urine / Swab / Stool
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  Standard / STAT options
                </Badge>
                <Badge size="xs" variant="light" color="green">
                  Most insurance accepted
                </Badge>
              </Group>
            </div>
          </Group>
        </Paper>

        {/* Quest Diagnostics General Requisition Card */}
        <Paper
          p="lg"
          withBorder
          className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
          onClick={() => setView('quest')}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <img
              src={questLogo}
              alt="Quest Diagnostics"
              className="h-[40px] w-[120px] shrink-0 object-contain"
            />
            <div>
              <Group gap="xs" mb={4}>
                <Text fw={600} size="lg">
                  Quest Diagnostics
                </Text>
                <Badge color="teal" variant="light" size="sm">
                  Lab
                </Badge>
              </Group>
              <Text size="sm">
                Comprehensive laboratory testing including metabolic panels, hematology, thyroid,
                adrenal, diabetes, reproductive hormones, bone &amp; kidney markers, microbiology,
                and 4,500+ individual tests.
              </Text>
              <Group gap="xs" mt="sm">
                <Badge size="xs" variant="light" color="teal">
                  Blood / Urine / Swab / Stool
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  Standard / STAT options
                </Badge>
                <Badge size="xs" variant="light" color="green">
                  Most insurance accepted
                </Badge>
              </Group>
            </div>
          </Group>
        </Paper>

        {/* GTI Blood Test Requisition Card */}
        <Paper
          p="lg"
          withBorder
          className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
          onClick={() => setView('gti')}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <img src={gtiLogo} alt="GTI" className="h-[40px] w-[120px] shrink-0 object-contain" />
            <div>
              <Group gap="xs" mb={4}>
                <Text fw={600} size="lg">
                  GTI
                </Text>
                <Badge color="teal" variant="light" size="sm">
                  Lab
                </Badge>
              </Group>
              <Text size="sm">
                Genetic Technological Innovations blood work &mdash; lipid, anemia, thyroid,
                wellness, electrolyte, hepatic, renal and hormone panels, plus individual chemistry,
                hematology and endocrine tests.
              </Text>
              <Group gap="xs" mt="sm">
                <Badge size="xs" variant="light" color="teal">
                  Blood
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  Standard / STAT options
                </Badge>
                <Badge size="xs" variant="light" color="green">
                  Faxed to GTI
                </Badge>
              </Group>
            </div>
          </Group>
        </Paper>

        {/* Cologuard Colorectal Cancer Screening Card */}
        <Paper
          p="lg"
          withBorder
          className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
          onClick={() => setView('cologuard')}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <img
              src={exactSciencesLogo}
              alt="Exact Sciences"
              className="h-[40px] w-[120px] shrink-0 object-contain"
            />
            <div>
              <Group gap="xs" mb={4}>
                <Text fw={600} size="lg">
                  Exact Sciences (Cologuard)
                </Text>
                <Badge color="cyan" variant="light" size="sm">
                  Screening
                </Badge>
              </Group>
              <Text size="sm">
                Non-invasive colorectal cancer and advanced adenoma screening. FDA-approved for
                average-risk adults 45+. Kit shipped directly to patient.
              </Text>
              <Group gap="xs" mt="sm">
                <Badge size="xs" variant="light" color="cyan">
                  Stool sample
                </Badge>
                <Badge
                  size="xs"
                  variant="light"
                  color="orange"
                  leftSection={<IconPackage size={10} />}
                >
                  Kit shipped to patient
                </Badge>
                <Badge size="xs" variant="light" color="green">
                  Medicare covered (45+)
                </Badge>
              </Group>
            </div>
          </Group>
        </Paper>
        {/* Radiology Order Card */}
        <Paper
          p="lg"
          withBorder
          className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
          onClick={() => setView('radiology')}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <div className="flex h-[40px] w-[120px] shrink-0 items-center justify-center">
              <IconRadar2 size={36} className="text-[#0097CE]" />
            </div>
            <div>
              <Group gap="xs" mb={4}>
                <Text fw={600} size="lg">
                  Radiology
                </Text>
                <Badge color="blue" variant="light" size="sm">
                  Imaging
                </Badge>
              </Group>
              <Text size="sm">
                Order radiology procedures including CT, MRI, ultrasound, X-ray, nuclear medicine,
                PET scans, and interventional radiology. Select procedures from a comprehensive
                compendium and choose a nearby hospital facility.
              </Text>
              <Group gap="xs" mt="sm">
                <Badge size="xs" variant="light" color="blue">
                  CT / MRI / Ultrasound / X-Ray
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  375+ procedures
                </Badge>
                <Badge size="xs" variant="light" color="green">
                  Florida hospitals
                </Badge>
              </Group>
            </div>
          </Group>
        </Paper>
        {procedureReferralsV1 && (
          <Paper
            p="lg"
            withBorder
            className="cursor-pointer transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50/50 hover:shadow-lg"
            onClick={() => setView('referralChoice')}
          >
            <Group gap="md" wrap="nowrap" align="flex-start">
              <div className="flex h-[40px] w-[120px] shrink-0 items-center justify-center">
                <IconStethoscope size={36} className="text-[#6B21A8]" />
              </div>
              <div>
                <Group gap="xs" mb={4}>
                  <Text fw={600} size="lg">
                    Procedures / Referrals
                  </Text>
                  <Badge color="indigo" variant="light" size="sm">
                    Referral
                  </Badge>
                </Group>
                <Text size="sm">
                  Refer a patient to a specialist, or write an unspecified referral the patient
                  takes to any in-network provider or diagnostic center.
                </Text>
                <Group gap="xs" mt="sm">
                  <Badge size="xs" variant="light" color="indigo">
                    Specialist Referral
                  </Badge>
                  <Badge size="xs" variant="light" color="grape">
                    Unspecified Referral
                  </Badge>
                </Group>
              </div>
            </Group>
          </Paper>
        )}
      </Stack>
    </div>
  )
}

function Fallback() {
  return (
    <div className="flex h-full items-center justify-center">
      <Loader size="sm" />
    </div>
  )
}
